import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  theme: ThemeMode;
  resolved: ResolvedTheme;
  setTheme: (theme: ThemeMode) => void;
  cycle: () => void;
}

const STORAGE_KEY = 'mibodega.admin.theme';

function readStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 'system';
    const parsed = JSON.parse(raw) as { state?: { theme?: ThemeMode } };
    const t = parsed?.state?.theme;
    if (t === 'light' || t === 'dark' || t === 'system') return t;
  } catch {
    /* localStorage puede fallar en modo privado; usamos system como fallback. */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(theme: ThemeMode): ResolvedTheme {
  if (theme === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return theme;
}

function applyToDocument(resolved: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      resolved: 'light',
      setTheme: (theme) => {
        const next = resolve(theme);
        applyToDocument(next);
        set({ theme, resolved: next });
      },
      cycle: () => {
        const order: ThemeMode[] = ['light', 'dark', 'system'];
        const current = get().theme;
        const idx = order.indexOf(current);
        const nextTheme = order[(idx + 1) % order.length] ?? 'system';
        const nextResolved = resolve(nextTheme);
        applyToDocument(nextResolved);
        set({ theme: nextTheme, resolved: nextResolved });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (persisted) => {
        const theme = persisted?.theme ?? 'system';
        const next = resolve(theme);
        applyToDocument(next);
        useThemeStore.setState({ theme, resolved: next });
      },
    },
  ),
);

let mediaListenerAttached = false;

export function initTheme(): ResolvedTheme {
  const stored = readStoredTheme();
  const next = resolve(stored);
  applyToDocument(next);
  useThemeStore.setState({ theme: stored, resolved: next });

  if (!mediaListenerAttached && typeof window !== 'undefined' && window.matchMedia) {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (useThemeStore.getState().theme !== 'system') return;
      const nextResolved = mql.matches ? 'dark' : 'light';
      applyToDocument(nextResolved);
      useThemeStore.setState({ resolved: nextResolved });
    };
    mql.addEventListener('change', onChange);
    mediaListenerAttached = true;
  }

  return next;
}
